import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useToast } from '../../context/ToastContext';
import { KwitansiData } from '../../types';
import { User, CheckCircle, AlertTriangle } from 'lucide-react';

const fmtRp = (n: number) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;
const fmtTgl = (iso: string) =>
  iso
    ? new Date(String(iso).slice(0, 10) + 'T00:00:00').toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '-';

interface KwitansiModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaksiId: number | null;
}

function InfoRow({
  label,
  w = '8rem',
  children,
}: {
  label: string;
  w?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid items-start" style={{ gridTemplateColumns: `${w} 0.75rem 1fr` }}>
      <span className="whitespace-nowrap">{label}</span>
      <span>:</span>
      <span className="break-words">{children}</span>
    </div>
  );
}

function KwitansiItemView({
  kwitansi,
  copyLabel,
}: {
  kwitansi: KwitansiData;
  copyLabel?: string;
  compact?: boolean;
}) {
  const k: any = kwitansi;
  const m = k.madrasah || {};
  const tx = k.transaksi;
  const tg = k.tagihan;
  const sw = k.siswa || {};
  const lunas = tg.status === 'lunas';

  // Riwayat cicilan (jika belum dikirim server, tampilkan pembayaran ini saja)
  const riwayat: any[] =
    Array.isArray(k.riwayat) && k.riwayat.length > 0
      ? k.riwayat
      : [
          {
            id: tx.id,
            nomor_transaksi: tx.nomor_transaksi,
            tanggal_bayar: tx.tanggal_bayar,
            jumlah_bayar: tx.jumlah_bayar,
            metode: tx.metode,
            catatan: tx.catatan,
            kasir_nama: k.kasirNama,
          },
        ];
  const totalDibayar = riwayat.reduce((s, r) => s + Number(r.jumlah_bayar || 0), 0);

  const kopBaris2 = m.alamat || '';
  const kopBaris3 = [
    m.nsm && m.nsm !== '-' ? `NSM: ${m.nsm}` : '',
    m.npsn ? `NPSN: ${m.npsn}` : '',
    m.telepon ? `Telp. ${m.telepon}` : '',
  ].filter(Boolean).join(' • ');
  const kopBaris4 = [
    m.website ? `Website: ${m.website}` : '',
    m.email ? `Email: ${m.email}` : '',
  ].filter(Boolean).join('   ');

  const kota = m.kabupaten_kota
    ? `${String(m.kabupaten_kota).replace(/^(Kota|Kabupaten|Kab\.)\s+/i, '')}, `
    : '';

  return (
    <div className="bg-white p-4 sm:p-5 rounded-xl border border-slate-300 print:border-none print:p-0 text-slate-900 font-sans text-[11px]">
      {/* Status Dibatalkan */}
      {tx.status === 'dibatalkan' && (
        <div className="mb-2 p-2 bg-rose-50 border border-rose-200 rounded-lg flex items-center gap-2 text-rose-700 text-xs font-semibold">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          TRANSAKSI INI TELAH DIBATALKAN (VOID). Alasan: {tx.alasan_batal || '-'}
        </div>
      )}

      {/* Label lembar */}
      {copyLabel && (
        <div className="flex justify-between items-center text-[10px] uppercase font-bold text-slate-500 mb-2">
          <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded border border-slate-300">
            {copyLabel}
          </span>
        </div>
      )}

      {/* KOP SURAT */}
      <div className="kop-surat border-b border-slate-700 pb-1.5 mb-3 flex items-center gap-3">
        {m.logo_url && (
          <img
            src={m.logo_url}
            alt=""
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            className="kop-logo w-14 h-14 object-contain shrink-0"
          />
        )}
        <div className="flex-1 text-left">
          <h2 className="text-xl font-extrabold uppercase tracking-wide text-slate-900 leading-tight">
            {m.nama || 'Madrasah'}
          </h2>
          {kopBaris2 && <p className="text-[9px] font-bold leading-snug">{kopBaris2}</p>}
          {kopBaris3 && <p className="text-[9px] font-bold leading-snug">{kopBaris3}</p>}
          {kopBaris4 && <p className="text-[9px] font-bold leading-snug">{kopBaris4}</p>}
        </div>
      </div>

      {/* Judul */}
      <h3 className="text-center text-base font-extrabold tracking-wide mb-2">
        PENERIMAAN PEMBAYARAN
      </h3>

            {/* Info umum */}
      <div className="space-y-0.5 mb-3 font-semibold">
        <InfoRow label="No. Kwitansi">
          <span className="font-mono">{tx.nomor_transaksi}</span>
        </InfoRow>
        <InfoRow label="Tahun Buku">{k.tahunAjaran || '-'}</InfoRow>
        <InfoRow label="Kategori">Iuran Wajib Siswa</InfoRow>
        <InfoRow label="Jenis Penerimaan">{tg.jenisPembayaran?.nama || '-'}</InfoRow>
      </div>

      {/* Dua kotak: pembayaran & data siswa */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="border border-slate-400 rounded">
          <div className="text-center text-slate-500 font-bold border-b border-slate-300 py-1 bg-slate-50">
            Pembayaran yang Harus Dilunasi
          </div>
          <div className="p-2 space-y-0.5 font-semibold">
            <InfoRow label="Pembayaran" w="5.5rem">
              {tg.jenisPembayaran?.nama || '-'}{tg.bulan ? ` (${tg.bulan})` : ''}
            </InfoRow>
            <InfoRow label="Jumlah" w="5.5rem">{fmtRp(tg.nominal)}</InfoRow>
            <InfoRow label="Status" w="5.5rem">
              <span className={lunas ? 'text-emerald-700' : 'text-rose-600'}>
                {lunas ? 'Lunas' : 'Belum Lunas'}
              </span>
            </InfoRow>
            <InfoRow label="Keterangan" w="5.5rem">{tx.catatan || '-'}</InfoRow>
          </div>
        </div>

        <div className="border border-slate-400 rounded">
          <div className="text-center text-slate-500 font-bold border-b border-slate-300 py-1 bg-slate-50">
            Data Siswa
          </div>
          <div className="p-2 flex gap-2">
            <div className="flex-1 space-y-0.5 font-semibold">
              <InfoRow label="NIS" w="3.5rem">{sw.nis || '-'}</InfoRow>
              <InfoRow label="Nama" w="3.5rem">{sw.nama || '-'}</InfoRow>
              <InfoRow label="Kelas" w="3.5rem">{sw.kelas?.nama || '-'}</InfoRow>
              <InfoRow label="Telepon" w="3.5rem">{sw.telepon_ortu || '-'}</InfoRow>
              <InfoRow label="Alamat" w="3.5rem">{sw.alamat || '-'}</InfoRow>
            </div>
            <div className="w-14 h-[4.5rem] border border-slate-300 bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
              {sw.foto_url ? (
                <img src={sw.foto_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-7 h-7 text-slate-400" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabel pembayaran / cicilan */}
      <p className="font-bold text-emerald-800 mb-1">Pembayaran Cicilan</p>
      <table className="w-full border-collapse border border-slate-400 mb-2">
        <thead>
          <tr className="bg-slate-100 text-slate-500">
            <th className="border border-slate-300 py-1 px-1.5 w-8">No</th>
            <th className="border border-slate-300 py-1 px-1.5">No. Kwitansi / Tgl</th>
            <th className="border border-slate-300 py-1 px-1.5">Metode</th>
            <th className="border border-slate-300 py-1 px-1.5 text-right">Besar</th>
            <th className="border border-slate-300 py-1 px-1.5">Keterangan</th>
            <th className="border border-slate-300 py-1 px-1.5">Petugas</th>
          </tr>
        </thead>
        <tbody>
          {riwayat.map((r, i) => (
            <tr key={r.id} className={r.id === tx.id ? 'font-bold' : ''}>
              <td className="border border-slate-300 py-1 px-1.5 text-center">{i + 1}</td>
              <td className="border border-slate-300 py-1 px-1.5 text-center">
                <span className="font-mono block">{r.nomor_transaksi}</span>
                <span className="italic text-[10px] font-normal">{fmtTgl(r.tanggal_bayar)}</span>
              </td>
              <td className="border border-slate-300 py-1 px-1.5 text-center">{r.metode}</td>
              <td className="border border-slate-300 py-1 px-1.5 text-right">{fmtRp(r.jumlah_bayar)}</td>
              <td className="border border-slate-300 py-1 px-1.5">
                {r.catatan || (tg.bulan ? String(tg.bulan).toUpperCase() : '')}
              </td>
              <td className="border border-slate-300 py-1 px-1.5">{r.kasir_nama}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-bold text-slate-600">
            <td colSpan={3} className="border border-slate-300 py-1 px-1.5 text-right tracking-widest">
              T O T A L
            </td>
            <td className="border border-slate-300 py-1 px-1.5 text-right">{fmtRp(totalDibayar)}</td>
            <td colSpan={2} className="border border-slate-300 py-1 px-1.5 text-slate-500">
              Sisa {fmtRp(tg.sisa)}
            </td>
          </tr>
        </tfoot>
      </table>

      <p className="mb-2">
        <span className="font-bold">Terbilang (pembayaran ini): </span>
        <span className="italic font-serif">"{k.terbilang}"</span>
      </p>

          {/* TANDA TANGAN */}
      <div className="signature-block print-avoid-break grid grid-cols-2 gap-4 text-center text-[11px] mt-3">
        <div className="flex flex-col items-center">
          <p className="text-slate-500">&nbsp;</p>
          <p className="font-semibold">Penyetor / Siswa</p>
          <div className="h-12 signature-space" />
          <p className="font-bold border-t border-slate-400 px-4 min-w-[11rem]">
            ( {sw.nama} )
          </p>
        </div>

        <div className="flex flex-col items-center">
          <p className="text-slate-500">
            {kota}
            {new Date(tx.tanggal_bayar).toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
          <p className="font-semibold">Petugas Kasir / Bendahara</p>
          <div className="h-12 flex items-center justify-center signature-space">
            {lunas ? (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                <CheckCircle className="w-3 h-3" /> LUNAS
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                DITERIMA (CICILAN)
              </span>
            )}
          </div>
          <p className="font-bold border-t border-slate-400 px-4 min-w-[11rem]">{k.kasirNama}</p>
        </div>
      </div>

      <div className="mt-2 pt-1 border-t border-slate-200 text-center text-[8.5px] text-slate-400">
        * Bukti kwitansi sah yang dikeluarkan oleh Sistem Informasi Akademik & Keuangan Madrasah.
      </div>
    </div>
  );
}

export function KwitansiModal({ isOpen, onClose, transaksiId }: KwitansiModalProps) {
  const { error } = useToast();

  const { data: kwitansi, isLoading } = useQuery({
    queryKey: ['kwitansi', transaksiId],
    queryFn: async () => {
      if (!transaksiId) return null;
      const res = await api.get<KwitansiData>(`/api/keuangan/pembayaran/${transaksiId}/kwitansi`);
      return res.data || null;
    },
    enabled: Boolean(transaksiId && isOpen),
  });

  // Data siap -> langsung buka dialog cetak, lalu tutup setelah selesai / dibatalkan
  useEffect(() => {
    if (!isOpen || !kwitansi) return;
    const selesai = () => onClose();
    window.addEventListener('afterprint', selesai, { once: true });
    const t = setTimeout(() => window.print(), 300);
    return () => {
      clearTimeout(t);
      window.removeEventListener('afterprint', selesai);
    };
  }, [isOpen, kwitansi]);

  // Data tidak ditemukan -> beri tahu dan tutup
  useEffect(() => {
    if (isOpen && !isLoading && !kwitansi) {
      error('Data kwitansi tidak ditemukan.');
      onClose();
    }
  }, [isOpen, isLoading, kwitansi]);

  if (!isOpen || !kwitansi) return null;

  return (
    <>
      <style>{`
        @media print {
          @page { size: auto; margin: 8mm; }
          html, body, #root {
            height: auto !important;
            overflow: visible !important;
            background: #fff !important;
          }
          body * { visibility: hidden !important; }
          .laporan-sheet, .jadwal-sheet, .absensi-sheet, .nilai-sheet,
          .rapor-sheet, .tunggakan-sheet, .kartu-spp-sheet { display: none !important; }
          .kwitansi-print-sheet, .kwitansi-print-sheet * { visibility: visible !important; }
          .kwitansi-print-sheet {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }
          .kwitansi-copy { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      <div className="kwitansi-print-sheet hidden print:block text-slate-900">
        <div className="kwitansi-copy">
          <KwitansiItemView kwitansi={kwitansi} compact />
        </div>
      </div>
    </>
  );
}
